import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Edit, Trash, X, Check, Printer } from 'lucide-react';
import CustomAlert from '../CustomAlert';

const apiUrl = process.env.REACT_APP_API_URL;

// One colour per status, applied to both the closed control and each row of the
// open dropdown. Unknown values (legacy rows) fall back to gray.
const STATUS_STYLES = {
  complete: 'bg-green-100 text-green-800',
  draft: 'bg-yellow-100 text-yellow-800',
};
const statusClass = (status) => STATUS_STYLES[status] || 'bg-gray-100 text-gray-800';

const InvoicePreview = () => {
  const { id: invoiceId } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [items, setItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [newPayment, setNewPayment] = useState({
    amount: '',
    payment_date: new Date().toISOString().slice(0, 10),
    payment_method: 'bank',
    note: ''
  });

  // Custom Alert State
  const [alert, setAlert] = useState({
    isOpen: false,
    type: 'error',
    title: '',
    message: '',
    details: ''
  });

  // Delete Confirmation State
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    type: 'error',
    title: 'Delete Invoice',
    message: 'Are you sure you want to delete this invoice? This action cannot be undone.',
    details: ''
  });

  const showAlert = (type, title, message, details = '') => {
    setAlert({
      isOpen: true,
      type,
      title,
      message,
      details
    });
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, isOpen: false }));
  };

  // Fetch invoice data
  useEffect(() => {
    const fetchInvoiceData = async () => {
      if (!invoiceId) return;
      
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          showAlert('error', 'Authentication Required', 'Please login to view invoices.');
          return;
        }

        const response = await fetch(`${apiUrl}/invoice-view/${invoiceId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setInvoice(data.invoice);
          setItems(data.items || []);
        } else {
          throw new Error('Failed to fetch invoice data');
        }
      } catch (error) {
        console.error('Error fetching invoice:', error);
        showAlert('error', 'Error Loading Invoice', 'Could not load invoice data.', error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoiceData();
  }, [invoiceId]);

  // Fetch payments for this invoice
  useEffect(() => {
    const fetchPayments = async () => {
      if (!invoiceId) return;
      
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${apiUrl}/invoice/${invoiceId}/payments`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setPayments(data);
        }
      } catch (error) {
        console.error('Error fetching payments:', error);
        // Don't show error for payments as the table might not exist yet
      }
    };

    fetchPayments();
  }, [invoiceId]);

  // Calculate totals
  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => {
      const quantity = parseFloat(item.quantity || 0);
      const price = parseFloat(item.price || 0);
      return sum + (quantity * price);
    }, 0);

    const tax = items.reduce((sum, item) => {
      const quantity = parseFloat(item.quantity || 0);
      const price = parseFloat(item.price || 0);
      const taxRate = parseFloat(item.tax_rate || 18);
      return sum + ((quantity * price) * (taxRate / 100));
    }, 0);

    const total = subtotal + tax;
    const totalPaid = payments.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
    const balance = total - totalPaid;

    return { subtotal, tax, total, totalPaid, balance };
  };

  const { subtotal, tax, total, totalPaid, balance } = calculateTotals();

  // Handle changing the invoice status
  const handleStatusChange = async (newStatus) => {
    const previousStatus = invoice.status;
    if (newStatus === previousStatus) return;

    // Recolour the badge straight away, and roll back if the save fails.
    setInvoice(prev => ({ ...prev, status: newStatus }));
    setSavingStatus(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Please login to change the status.');
      }

      const response = await fetch(`${apiUrl}/invoice/${invoiceId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update status: ${response.status}`);
      }

      showAlert('success', 'Status Updated', `Invoice status changed to ${newStatus}.`);
    } catch (error) {
      console.error('Error updating status:', error);
      setInvoice(prev => ({ ...prev, status: previousStatus }));
      showAlert('error', 'Error Updating Status', 'Could not update the invoice status.', error.message);
    } finally {
      setSavingStatus(false);
    }
  };

  // Handle adding new payment
  const handleAddPayment = async (e) => {
    e.preventDefault();
    
    if (!newPayment.amount || parseFloat(newPayment.amount) <= 0) {
      showAlert('error', 'Invalid Amount', 'Please enter a valid payment amount.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showAlert('error', 'Authentication Required', 'Please login to add payments.');
        return;
      }

      const response = await fetch(`${apiUrl}/invoice/${invoiceId}/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...newPayment,
          amount: parseFloat(newPayment.amount)
        }),
      });

      if (response.ok) {
        const addedPayment = await response.json();
        setPayments(prev => [...prev, addedPayment]);
        setNewPayment({
          amount: '',
          payment_date: new Date().toISOString().slice(0, 10),
          payment_method: 'cash',
          note: ''
        });
        setShowPaymentForm(false);
        showAlert('success', 'Payment Added', `Payment of ${parseFloat(newPayment.amount).toFixed(2)} ден added successfully.`);
      } else {
        throw new Error('Failed to add payment');
      }
    } catch (error) {
      console.error('Error adding payment:', error);
      showAlert('error', 'Error Adding Payment', 'Could not add payment.', error.message);
    }
  };

  // Handle deleting a payment
  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showAlert('error', 'Authentication Required', 'Please login to delete payments.');
        return;
      }

      const response = await fetch(`${apiUrl}/invoice/${invoiceId}/payments/${paymentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setPayments(prev => prev.filter(payment => payment.id !== paymentId));
        showAlert('success', 'Payment Deleted', 'Payment has been deleted successfully.');
      } else {
        throw new Error('Failed to delete payment');
      }
    } catch (error) {
      console.error('Error deleting payment:', error);
      showAlert('error', 'Error Deleting Payment', 'Could not delete payment.', error.message);
    }
  };

  // Handle editing a payment
  const handleEditPayment = async (paymentId, updatedPayment) => {
    if (!updatedPayment.amount || parseFloat(updatedPayment.amount) <= 0) {
      showAlert('error', 'Invalid Amount', 'Please enter a valid payment amount.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showAlert('error', 'Authentication Required', 'Please login to edit payments.');
        return;
      }

      const response = await fetch(`${apiUrl}/invoice/${invoiceId}/payments/${paymentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...updatedPayment,
          amount: parseFloat(updatedPayment.amount)
        }),
      });

      if (response.ok) {
        const updatedPaymentData = await response.json();
        setPayments(prev => prev.map(payment => 
          payment.id === paymentId ? updatedPaymentData : payment
        ));
        setEditingPayment(null);
        showAlert('success', 'Payment Updated', `Payment updated successfully.`);
      } else {
        throw new Error('Failed to update payment');
      }
    } catch (error) {
      console.error('Error updating payment:', error);
      showAlert('error', 'Error Updating Payment', 'Could not update payment.', error.message);
    }
  };

  // Start editing a payment
  const startEditPayment = (payment) => {
    setEditingPayment({
      id: payment.id,
      amount: payment.amount.toString(),
      payment_date: payment.payment_date,
      payment_method: payment.payment_method,
      note: payment.note || ''
    });
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingPayment(null);
  };

  // Handle showing delete confirmation
  const handleDeleteInvoice = () => {
    setDeleteConfirmation(prev => ({ ...prev, isOpen: true }));
  };

  // Handle actual deletion after confirmation
  const confirmDeleteInvoice = async () => {
    setDeleteConfirmation(prev => ({ ...prev, isOpen: false }));
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showAlert('error', 'Authentication Required', 'Please login to delete invoices.');
        return;
      }

      const response = await fetch(`${apiUrl}/invoice/${invoiceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        showAlert('success', 'Invoice Deleted', 'Invoice has been deleted successfully.');
        // Navigate back to invoice list after a short delay
        setTimeout(() => {
          navigate('/invoices/list');
        }, 2000);
      } else {
        throw new Error('Failed to delete invoice');
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      showAlert('error', 'Error Deleting Invoice', 'Could not delete invoice.', error.message);
    }
  };

  // Handle cancel deletion
  const cancelDeleteInvoice = () => {
    setDeleteConfirmation(prev => ({ ...prev, isOpen: false }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-gray-600">Loading invoice...</div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-red-600">Invoice not found</div>
      </div>
    );
  }

  return (
    <div>
      {/* Settings/Actions Bar - spans the full page width; the content below
          stays inside the max-w-7xl column. */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            {/* Left Side - Navigation & Primary Actions */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/invoices/list')}
                className="flex items-center gap-1 px-2 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to List
              </button>
              
              <div className="h-6 w-px bg-gray-300"></div>
              
              <button
                onClick={() => navigate(`/invoices/edit/${invoiceId}`)}
                className="flex items-center gap-1 px-2 py-1 text-sm bg-indigo-600 text-white hover:bg-indigo-700 rounded-md transition-colors"
              >
                <Edit size={14} />
                Edit Invoice
              </button>
              
              <button
                onClick={() => window.open(`${window.location.origin}/preview/invoice/${invoiceId}`, '_blank')}
                className="flex items-center gap-1 px-2 py-1 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors"
              >
                <Printer size={14} />
                Преглед
              </button>

              <button
                onClick={() => {
                  if (!invoice.pdf_filename) {
                    showAlert('error', 'No PDF Available', 'No PDF has been generated for this invoice yet.');
                    return;
                  }
                  const pdfUrl = `${window.location.origin}/preview/${invoice.occupier}/invoices/${encodeURIComponent(invoice.pdf_filename)}`;
                  window.open(pdfUrl, '_blank');
                }}
                disabled={!invoice.pdf_filename}
                className="flex items-center gap-1 px-2 py-1 text-sm bg-green-600 text-white hover:bg-green-700 rounded-md transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                View PDF
              </button>
            </div>

            {/* Right Side - Status & View Options */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Status:</span>
                <select
                  value={invoice.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={savingStatus}
                  className={`inline-flex px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed ${statusClass(invoice.status)}`}
                >
                  {Object.keys(STATUS_STYLES).map((status) => (
                    <option key={status} value={status} className={`${statusClass(status)} font-medium`}>
                      {status}
                    </option>
                  ))}
                  {/* Keep a legacy value visible rather than silently showing
                      the wrong selection. */}
                  {!STATUS_STYLES[invoice.status] && (
                    <option value={invoice.status} className={statusClass(invoice.status)}>
                      {invoice.status}
                    </option>
                  )}
                </select>
              </div>
              
              <div className="h-6 w-px bg-gray-300"></div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Payment Status:</span>
                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                  balance <= 0 && totalPaid > 0
                    ? 'bg-green-100 text-green-800'
                    : totalPaid > 0
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {balance <= 0 && totalPaid > 0 ? 'Paid' : totalPaid > 0 ? 'Partial' : 'Unpaid'}
                </span>
              </div>
            </div>
          </div>
      </div>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Invoice Preview - Left Side (2/3 width) */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg p-6">
              {/* Invoice Header */}
              <div className="border-b border-gray-200 pb-6 mb-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">
                      Invoice #{invoice.invoice_number}
                    </h1>
                    <p className="text-gray-600 mt-2">
                      Date: {new Date(invoice.date || invoice.invoice_date).toLocaleDateString()}
                    </p>
                    {/* <div className="mt-2">
                      <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                        invoice.status === 'complete'
                          ? 'bg-green-100 text-green-800'
                          : invoice.status === 'draft'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {invoice.status}
                      </span>
                    </div> */}
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-gray-900">
                      Total: {total.toFixed(2)} ден
                    </div>
                    {balance > 0 && (
                      <div className="text-red-600 font-medium">
                        Balance Due: {balance.toFixed(2)} ден
                      </div>
                    )}
                    {balance === 0 && totalPaid > 0 && (
                      <div className="text-green-600 font-medium">
                        Paid in Full
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Bill To:</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-medium text-gray-900">{invoice.customer_name}</p>
                  {invoice.address && (
                    <p className="text-gray-600 mt-1">{invoice.address}</p>
                  )}
                  {invoice.phone && (
                    <p className="text-gray-600 mt-1">Phone: {invoice.phone}</p>
                  )}
                  {invoice.email && (
                    <p className="text-gray-600 mt-1">Email: {invoice.email}</p>
                  )}
                </div>
              </div>

              {/* Items Table */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Items:</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-300 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">#</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b">Product</th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 border-b">Qty</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-700 border-b">Price</th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 border-b">Tax %</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-700 border-b">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => {
                        const quantity = parseFloat(item.quantity || 0);
                        const price = parseFloat(item.price || 0);
                        const taxRate = parseFloat(item.tax_rate || 18);
                        const itemSubtotal = quantity * price;
                        const itemTax = itemSubtotal * (taxRate / 100);
                        const itemTotal = itemSubtotal + itemTax;

                        return (
                          <tr key={item.id || index} className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">{index + 1}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{item.product_name || item.name}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-center">{quantity}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right">{price.toFixed(2)}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-center">{taxRate}%</td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">{itemTotal.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals Summary */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-medium">{subtotal.toFixed(2)} ден</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Tax:</span>
                      <span className="font-medium">{tax.toFixed(2)} ден</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span>Total:</span>
                      <span>{total.toFixed(2)} ден</span>
                    </div>
                    {totalPaid > 0 && balance > 0 && (
                      <>
                        <div className="flex justify-between text-sm text-green-600">
                          <span>Paid:</span>
                          <span className="font-medium">-{totalPaid.toFixed(2)} ден</span>
                        </div>
                        <div className="flex justify-between text-lg font-bold border-t pt-2">
                          <span className="text-red-600">
                            Balance:
                          </span>
                          <span className="text-red-600">
                            {balance.toFixed(2)} ден
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payments Panel - Right Side (1/3 width) */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Payments</h3>
                <button
                  onClick={() => setShowPaymentForm(!showPaymentForm)}
                  className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-700 transition-colors"
                >
                  + Add Payment
                </button>
              </div>

              {/* Add Payment Form */}
              {showPaymentForm && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
                  <form onSubmit={handleAddPayment} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Amount (ден)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max={balance}
                        value={newPayment.amount}
                        onChange={(e) => setNewPayment(prev => ({ ...prev, amount: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Date
                      </label>
                      <input
                        type="date"
                        value={newPayment.payment_date}
                        onChange={(e) => setNewPayment(prev => ({ ...prev, payment_date: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Method
                      </label>
                      <select
                        value={newPayment.payment_method}
                        onChange={(e) => setNewPayment(prev => ({ ...prev, payment_method: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="bank">Bank Transfer</option>
                        <option value="cash">Cash</option>
                        <option value="check">Check</option>
                        <option value="card">Card</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Note (Optional)
                      </label>
                      <textarea
                        value={newPayment.note}
                        onChange={(e) => setNewPayment(prev => ({ ...prev, note: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={2}
                        placeholder="Payment note..."
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors text-sm"
                      >
                        Add Payment
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowPaymentForm(false)}
                        className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Payments List */}
              <div className="space-y-3">
                {payments.length > 0 ? (
                  payments.map((payment, index) => (
                    <div key={payment.id || index} className="p-3 bg-gray-50 rounded-lg border">
                      {editingPayment && editingPayment.id === payment.id ? (
                        // Edit Mode
                        <form onSubmit={(e) => {
                          e.preventDefault();
                          handleEditPayment(payment.id, editingPayment);
                        }} className="space-y-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Amount (ден)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max={balance + parseFloat(payment.amount)}
                              value={editingPayment.amount}
                              onChange={(e) => setEditingPayment(prev => ({ ...prev, amount: e.target.value }))}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Payment Date
                            </label>
                            <input
                              type="date"
                              value={editingPayment.payment_date}
                              onChange={(e) => setEditingPayment(prev => ({ ...prev, payment_date: e.target.value }))}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Payment Method
                            </label>
                            <select
                              value={editingPayment.payment_method}
                              onChange={(e) => setEditingPayment(prev => ({ ...prev, payment_method: e.target.value }))}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="bank_transfer">Bank Transfer</option>
                              <option value="cash">Cash</option>
                              <option value="check">Check</option>
                              <option value="card">Card</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Note (Optional)
                            </label>
                            <textarea
                              value={editingPayment.note}
                              onChange={(e) => setEditingPayment(prev => ({ ...prev, note: e.target.value }))}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              rows={2}
                              placeholder="Payment note..."
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="submit"
                              className="flex-1 bg-green-600 text-white py-1 px-2 rounded text-xs hover:bg-green-700 transition-colors flex items-center justify-center gap-1"
                            >
                              <Check size={14} /> Save
                            </button>
                            <button
                              type="button"
                              onClick={cancelEdit}
                              className="flex-1 bg-gray-300 text-gray-700 py-1 px-2 rounded text-xs hover:bg-gray-400 transition-colors flex items-center justify-center gap-1"
                            >
                              <X size={14} /> Cancel
                            </button>
                          </div>
                        </form>
                      ) : (
                        // Display Mode
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">
                              {parseFloat(payment.amount).toFixed(2)} ден
                            </div>
                            <div className="text-sm text-gray-600">
                              {new Date(payment.payment_date).toLocaleDateString()}
                            </div>
                            <div className="text-xs text-gray-500 capitalize">
                              {payment.payment_method?.replace('_', ' ')}
                            </div>
                            {payment.note && (
                              <div className="text-xs text-gray-600 mt-1">
                                {payment.note}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex gap-1">
                              <button
                                onClick={() => startEditPayment(payment)}
                                className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition-colors"
                                title="Edit Payment"
                              >
                                <Edit size={14} />
                              </button>
                              <button
                                onClick={() => handleDeletePayment(payment.id)}
                                className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition-colors"
                                title="Delete Payment"
                              >
                                <Trash size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <div className="text-4xl mb-2">💳</div>
                    <div className="text-sm">No payments recorded</div>
                    <div className="text-xs text-gray-400 mt-1">
                      Add a payment to track invoice progress
                    </div>
                  </div>
                )}
              </div>

              {/* Payment Summary */}
              {payments.length > 0 && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Paid:</span>
                      <span className="font-medium text-green-600">
                        {totalPaid.toFixed(2)} ден
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Remaining:</span>
                      <span className={`font-medium ${balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {balance.toFixed(2)} ден
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Standalone Delete Icon */}
        <div className="flex justify-end mt-4">
          <button
            onClick={handleDeleteInvoice}
            className="p-2 text-red-600 hover:text-red-700 transition-colors"
            title="Delete Invoice"
          >
            <Trash size={20} />
          </button>
        </div>
      </div>

      {/* Custom Alert Modal */}
      <CustomAlert
        isOpen={alert.isOpen}
        onClose={closeAlert}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        details={alert.details}
      />

      {/* Delete Confirmation Modal */}
      <CustomAlert
        isOpen={deleteConfirmation.isOpen}
        onClose={cancelDeleteInvoice}
        type={deleteConfirmation.type}
        title={deleteConfirmation.title}
        message={deleteConfirmation.message}
        details={deleteConfirmation.details}
        onConfirm={confirmDeleteInvoice}
        showConfirmButton={true}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default InvoicePreview;